//region :: Imports ::
import React, {createRef, ReactElement, ReactNode, RefObject} from 'react';
import MComponent from '../../Toolkit/Components/Component';
import {MCancelIconName, MIcon, MOkIconName} from '../../Toolkit/Icons';
import {DialogPositionType} from 'primereact/dialog';
import Dialog from '../../Toolkit/Components/Controls/Dialog';
import MButton from '../../Toolkit/Components/Controls/Button';
import MDropDown from '../../Toolkit/Components/Controls/DropDown';
import {MDropDownItem} from '../../Toolkit/Components/Controls/DropDownOption';
import {MNullable} from '../../Toolkit/Utils';
import MFormControl from '../../Toolkit/Components/Controls/FormControl';
import MCalendar from '../../Toolkit/Components/Controls/Calendar';
import {UploadIconName} from '../SafeDigConstants';
import MTextBox from '../../Toolkit/Components/Controls/TextBox';
import EnumContext from '../../Context/enum-context';
import {SafeDigFile} from '../../Api/SafeDigFileTypes';

//endregion

/** */
type Props = {
    jobComplete: boolean;
    uploadPermission: boolean;
}

/** */
type State = {
    position?: DialogPositionType;
    visible: boolean;
    pageTitle: string;
    data: NoticeUploadData;
    fileInfoOptions: MDropDownItem<number>[];
    onUploadFile?: (data: NoticeUploadData) => void;
    uploading: boolean;
}

export type NoticeUploadData = {
    file: File | undefined;
    fileType: number | undefined;
    fileName: string | undefined;
    startDate: Date | undefined;
    expiryDate: Date | undefined;
}

/** */
class NoticeFileDialog extends MComponent<Props, State> {
    static contextType = EnumContext;
    context!: React.ContextType<typeof EnumContext>

    private UploadIcon: MIcon = {name: UploadIconName, color: 'mcolor-info'};
    private readonly m_dateRef: RefObject<MCalendar>;

    /** */
    public constructor(props: Props) {
        super(props);

        this.bindMethods();

        this.m_dateRef = createRef<MCalendar>();

        this.initializeState();
    }

    /** */
    private bindMethods(): void {
        this.dialog_onHide = this.dialog_onHide.bind(this);

        this.getDialogTemplate = this.getDialogTemplate.bind(this);
        this.onFileChange = this.onFileChange.bind(this);
        this.onFileTypeChange = this.onFileTypeChange.bind(this);
        this.getFooterTemplate = this.getFooterTemplate.bind(this);
        this.ok_Click = this.ok_Click.bind(this);

        this.show = this.show.bind(this);
        this.hide = this.hide.bind(this);
    }

    /** */
    private initializeState(): void {
        this.state = {
            visible: false,
            fileInfoOptions: [],
            pageTitle: 'Notice File Upload',
            uploading: false,
            data: {
                file: undefined,
                fileType: undefined,
                fileName: '',
                startDate: new Date(),
                expiryDate: new Date()
            }
        };
    }

    /** */
    private dialog_onHide(): void {
        this.safeSetState((draft: State) => draft.visible = false);
    }

    /** */
    public show(inputCallback: (data: NoticeUploadData) => void, fileType: number, position?: DialogPositionType) {

        var initialDate = new Date();

        this.safeSetState((draft: State) => {
            draft.position = position;
            draft.visible = true;
            draft.onUploadFile = inputCallback;
            draft.uploading = false;
            draft.data.fileType = fileType;
            draft.data.fileName = 'default';
            draft.data.startDate = initialDate;
            draft.data.expiryDate = initialDate;
            draft.data.file = undefined;
        });
    }

    public hide() {
        this.safeSetState((draft: State) => {
            draft.visible = false;
        });
    }

    private ok_Click() {
        if(!this.state.uploading){
            this.safeSetState((draft: State) => draft.uploading = true);
            this.state.onUploadFile!(this.state.data!);
        }
    }

    private getDialogTemplate(): ReactNode {
        const fixedFileTypes = [11, 12];

        const fileTypeList = this.context?.enums === null ? []
            : fixedFileTypes.includes(this.state.data.fileType ?? -1)
                ? this.context?.enums.noticeFileTypes
                : this.context?.enums.noticeFileTypes.filter(nf => !fixedFileTypes.includes(nf.id));

        return (
            <div className="formgrid grid">
                <div className="col-12 md:col-12">
                    <MFormControl label={'File Type'} id={'FileType'}>
                        {(fieldClasses: string | undefined) => {
                            return (
                                <MDropDown<number> id="FileTypeDropdown"
                                                   disabled={fixedFileTypes.includes(this.state.data.fileType ?? -1)}
                                                   onChange={this.onFileTypeChange}
                                                   items={fileTypeList === null ? [] : fileTypeList.map((enumvalue) => {
                                                       return {label: enumvalue.name, value: enumvalue.id};
                                                   })}
                                                   initialValue={this.state.data.fileType}
                                />
                            );
                        }}
                    </MFormControl>
                </div>
                <div className="col-12 md:col-12">
                    <MFormControl label={'File Name'} id={'FileNameText'}>
                        {(fieldClasses: string | undefined) => {
                            return (
                                <MTextBox id="FileNameEntry"
                                          maxLength={200}
                                          disabled={false}
                                          onChange={e => this.safeSetState((draft: State) => draft.data.fileName = e!.toString())}
                                />
                            );
                        }}
                    </MFormControl>
                </div>
                <div className="col-12 md:col-12">
                    <MFormControl label={'Start Date'} id={'startdate'}>
                        {(fieldClasses: string | undefined) => {
                            return (
                                <MCalendar id="FileStartDate"
                                           ref={this.m_dateRef}
                                           initialValue={this.state.data.startDate!}
                                           disabled={false}
                                           className={fieldClasses}
                                           onChange={e => this.safeSetState((draft: State) => draft.data.startDate = e!)}
                                />
                            );
                        }}
                    </MFormControl>
                </div>
                <div className="col-12 md:col-12">
                    <MFormControl label={'Expiry Date'} id={'expirydate'}>
                        {(fieldClasses: string | undefined) => {
                            return (
                                <MCalendar id="FileExpiryDate"
                                           ref={this.m_dateRef}
                                           initialValue={this.state.data.expiryDate!}
                                           disabled={false}
                                           className={fieldClasses}
                                           onChange={e => this.safeSetState((draft: State) => draft.data.expiryDate = e!)}
                                />
                            );
                        }}
                    </MFormControl>
                </div>
                <div>
                    <input type="file" onChange={this.onFileChange} accept={'.pdf,.doc,.docx,.xls,.xlsx'}/>
                </div>
            </div>);
    }

    private getFooterTemplate(): ReactNode {
        return (
            <div>
                <MButton label="OK"
                         color="success"
                         icon={MOkIconName}
                         disabled={!this.isUploadAvailable()}
                         onClick={this.ok_Click}/>
                <MButton label="Cancel"
                         icon={MCancelIconName}
                         disabled={false}
                         onClick={this.hide}/>
            </div>
        );
    }

    private onFileTypeChange(newValue: MNullable<number>) {
        this.safeSetState(
            (draft: State) => draft.data!.fileType = newValue!);
    }

    private onFileChange(event: React.ChangeEvent<HTMLInputElement>) {
        const fileTypes = ['.pdf','.doc','.docx','.xls','.xlsx'];

        if(event.target.files?.length) {
            const file = event.target.files[0];
            if(file.size <= SafeDigFile.MaxFileSizeMb * 1024 * 1024) {
                if(fileTypes.some(s => event.target.files![0].name.toLowerCase().endsWith(s))) {
                    this.safeSetState(
                        (draft: State) => draft.data!.file = event.target.files![0]);
                } else {
                    this.safeSetState(
                        (draft: State) => draft.data!.file = undefined);
                }
            } else {
                alert(`Files must be less than ${SafeDigFile.MaxFileSizeMb}Mb in size. Please rescale any images in the file and try again.`);
            }
        } else {
            this.safeSetState(
                (draft: State) => draft.data!.file = undefined);
        }
    }

    private isUploadAvailable() {
        return (this.props.uploadPermission) &&
            !this.state.uploading &&
            this.state.data.fileType !== undefined &&
            this.state.data!.fileName!.length > 0 &&
            this.state.data.startDate !== undefined &&
            this.state.data.expiryDate !== undefined &&
            this.state.data!.file !== undefined;
    }

    /** */
    public render(): ReactElement {

        return (
            <Dialog title={this.state.pageTitle}
                    content={this.getDialogTemplate()}
                    icon={this.UploadIcon}
                    footer={this.getFooterTemplate()}
                    position={this.state.position}
                    canClose={false}
                    closeOnEscape={false}
                    visible={this.state.visible}
                    onHide={this.dialog_onHide}/>
        );
    }
}

export default NoticeFileDialog;
